
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { generateVoucherCode, batchArray } from "@/utils/voucherUtils";

// Define a type for the campaign from Supabase
export type Campaign = {
  id: string;
  title: string;
  description: string;
  campaign_type: "unique" | "common";
  vouchers_total: number;
  vouchers_claimed: number;
  start_date: string;
  end_date: string;
  status: "active" | "scheduled" | "ended" | "draft";
  created_at: string;
  updated_at: string;
  voucher_image: string | null;
  voucher_details: string;
  sponsor_name: string;
  sponsor_message: string | null;
};

export function useCampaigns() {
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Use React Query to fetch campaigns
  const { data: campaigns = [], isLoading, error, refetch } = useQuery({
    queryKey: ['campaigns'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('campaigns')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        throw new Error(error.message);
      }
      
      return data as Campaign[];
    }
  });
  
  const publishCampaign = async (campaign: Campaign) => {
    try {
      setIsProcessing(true);
      
      // Determine status based on dates
      const now = new Date();
      const startDate = new Date(campaign.start_date);
      let status: 'active' | 'scheduled' = 'scheduled';
      
      if (startDate <= now) {
        status = 'active';
      }
      
      // Update campaign status
      const { error: updateError } = await supabase
        .from('campaigns')
        .update({
          status: status,
          updated_at: new Date().toISOString()
        })
        .eq('id', campaign.id);
      
      if (updateError) throw updateError;
      
      // Generate vouchers
      const voucherCodes = Array.from({ length: campaign.vouchers_total }, () => 
        generateVoucherCode()
      );
      
      const vouchers = voucherCodes.map(code => ({
        campaign_id: campaign.id,
        code,
        status: 'active'
      }));
      
      // Insert vouchers in batches to avoid query size limits
      const batchSize = 100;
      const batches = batchArray(vouchers, batchSize);
      
      for (const batch of batches) {
        const { error: vouchersError } = await supabase
          .from('vouchers')
          .insert(batch);
        
        if (vouchersError) throw vouchersError;
      }
      
      toast.success("Campaign published successfully!");
      await refetch(); // Refresh the campaigns list
      return true;
    } catch (error) {
      console.error("Error publishing campaign:", error);
      toast.error("Failed to publish campaign");
      return false;
    } finally {
      setIsProcessing(false);
    }
  };
  
  const deleteCampaign = async (campaignId: string) => {
    try {
      setIsProcessing(true);
      
      const { error } = await supabase
        .from('campaigns')
        .delete()
        .eq('id', campaignId);
      
      if (error) throw error;
      
      toast.success(`Campaign deleted successfully`);
      await refetch(); // Refresh the campaigns list
      return true;
    } catch (error) {
      console.error("Error deleting campaign:", error);
      toast.error("Failed to delete campaign");
      return false;
    } finally {
      setIsProcessing(false);
    }
  };
  
  const duplicateCampaign = async (campaign: Campaign) => {
    try {
      setIsProcessing(true);
      
      // Create a new campaign with the same data but a different title
      const { data: newCampaign, error: campaignError } = await supabase
        .from('campaigns')
        .insert({
          title: `${campaign.title} (Copy)`,
          description: campaign.description,
          campaign_type: campaign.campaign_type,
          vouchers_total: campaign.vouchers_total,
          vouchers_claimed: 0,
          start_date: campaign.start_date,
          end_date: campaign.end_date,
          voucher_image: campaign.voucher_image,
          voucher_details: campaign.voucher_details,
          sponsor_name: campaign.sponsor_name,
          sponsor_message: campaign.sponsor_message,
          status: 'draft'
        })
        .select()
        .single();
      
      if (campaignError) throw campaignError;
      
      // Get venue connections for the original campaign
      const { data: venueData, error: venueError } = await supabase
        .from('campaign_venues')
        .select('venue_id')
        .eq('campaign_id', campaign.id);
      
      if (venueError) throw venueError;
      
      // Copy venue connections to the new campaign
      if (venueData && venueData.length > 0) {
        const campaignVenues = venueData.map(item => ({
          campaign_id: newCampaign.id,
          venue_id: item.venue_id
        }));
        
        const { error: venuesInsertError } = await supabase
          .from('campaign_venues')
          .insert(campaignVenues);
        
        if (venuesInsertError) throw venuesInsertError;
      }
      
      toast.success("Campaign duplicated successfully!");
      await refetch(); // Refresh the campaigns list
      return true;
    } catch (error) {
      console.error("Error duplicating campaign:", error);
      toast.error("Failed to duplicate campaign");
      return false;
    } finally {
      setIsProcessing(false);
    }
  };
  
  return {
    campaigns,
    isLoading,
    error,
    refetch,
    isProcessing,
    publishCampaign,
    deleteCampaign,
    duplicateCampaign
  };
}
